import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";

import "@fontsource-variable/inter";
import { ThemeProvider } from "@/components/theme/theme-provider";
import { MonedaProvider } from "@/components/money";
import { AuthProvider } from "@/lib/auth-context";
import { PantallaConError } from "@/components/layout/PantallaConError";
import { COTIZACION_ARRANQUE } from "@/lib/cotizacion";
import { routeTree } from "./routeTree.gen";
import "./styles/globals.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/*
 * En el dominio de la tienda la direccion es `/` y la pantalla es el catalogo.
 * El backend marca el index con `superficie=tienda` (ver dominios.ts) y el
 * router traduce en los dos sentidos: lee `/` como `/catalogo` y escribe
 * `/catalogo` como `/`, asi la barra del navegador nunca muestra el sufijo.
 */
const esTienda =
  document.querySelector('meta[name="superficie"]')?.getAttribute("content") === "tienda";
const reescrituraTienda = {
  input: ({ url }: { url: URL }) => {
    if (url.pathname !== "/") return undefined;
    url.pathname = "/catalogo";
    return url;
  },
  output: ({ url }: { url: URL }) => {
    if (url.pathname !== "/catalogo") return undefined;
    url.pathname = "/";
    return url;
  },
};

// Un error al dibujar una pantalla se muestra en castellano y con «Recargar»,
// no con el «Something went wrong!» de TanStack.
const router = createRouter({
  routeTree,
  defaultErrorComponent: PantallaConError,
  rewrite: esTienda ? reescrituraTienda : undefined,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

/*
 * Despues de un deploy, una pestaña abierta de antes pide los pedazos que se
 * cargan con `import()` (jspdf, xlsx) con el nombre viejo: ya no existen y el
 * servidor devuelve el index.html ("'text/html' is not a valid JavaScript MIME
 * type"). Se recarga para traer la version nueva. Una sola vez por minuto: si
 * el pedazo falta de verdad, que se vea el error y no un bucle de recargas.
 */
window.addEventListener("vite:preloadError", (evento) => {
  const clave = "tora-recarga-por-deploy";
  try {
    const ultima = Number(sessionStorage.getItem(clave) ?? 0);
    if (Date.now() - ultima < 60_000) return;
    sessionStorage.setItem(clave, String(Date.now()));
  } catch {
    return;
  }
  evento.preventDefault();
  window.location.reload();
});

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("No se encontro #root");

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <ThemeProvider storageKey="tora-tema">
      <QueryClientProvider client={queryClient}>
        <MonedaProvider monedaInicial="ARS" cotizacionInicial={COTIZACION_ARRANQUE}>
          {/*
            El AuthProvider va ADENTRO del RouterProvider y no afuera: usa
            `useNavigate` para mandar al login o a /instalar, y eso necesita el
            contexto del router. Por eso se monta desde `InnerWrap`, que TanStack
            Router renderiza dentro del arbol de rutas.
          */}
          <RouterProvider router={router} InnerWrap={AuthProvider} />
        </MonedaProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>,
);
